/**
 * BulletItemEditor - Editor for a single bullet/feature item
 *
 * Renders editable fields per documentation:
 * - Image (URL input + upload button + settings gear)
 * - Title (text field, l10n)
 * - Subtitle (text field, l10n, optional)
 *
 * Layout:
 * ┌──────────────────────────────────────────────────────────┐
 * │ ITEM N                                              [🗑] │
 * │ ┌──────────────────────────────────┐ ┌──┐ ┌──┐          │
 * │ │ https://...                      │ │📷│ │⚙ │  Image   │
 * │ └──────────────────────────────────┘ └──┘ └──┘          │
 * │ Title                                                    │
 * │ [_____________________________________________]          │
 * │ Subtitle                                                 │
 * │ [_____________________________________________]          │
 * └──────────────────────────────────────────────────────────┘
 *
 * Uses local state buffering (blur-to-save pattern) to prevent focus loss.
 *
 * Sprint: screen-editor-paywall
 * Task: Fix Bullets Section
 */

import React, { useCallback, useState, useEffect, memo } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { MediaUploadField } from '@modules/ui-components';
import { PaywallVariableTextField } from '../../../shared/PaywallVariableTextField';
import { FIELD_GAP_SMALL, SPACING } from '../../../../config/ui';

/**
 * Bullet item data structure (l10n values extracted for current locale)
 */
export interface BulletItemData {
  id: string;           // For SortableList (e.g., "bullet-0")
  originalIndex: number;
  imageUrl: string;     // Image URL
  title: string;        // Title text (l10n extracted)
  subtitle: string;     // Subtitle text (l10n extracted)
}

/**
 * Props for BulletItemEditor
 */
export interface BulletItemEditorProps {
  /** Item index (0-based) */
  index: number;

  /** Current item data */
  item: BulletItemData;

  /** Callback when any field changes */
  onChange: (field: keyof BulletItemData, value: unknown) => void;

  /** Callback for image upload - returns URL */
  onImageUpload?: (file: File) => Promise<string>;

  /** Callback when delete button is clicked */
  onDelete?: () => void;

  /** Whether delete is allowed (false if minItems reached) */
  canDelete?: boolean;

  /** Whether editing is disabled */
  disabled?: boolean;
}

/**
 * BulletItemEditor - Card-like editor for a single bullet item
 */
const BulletItemEditorComponent: React.FC<BulletItemEditorProps> = ({
  index,
  item,
  onChange,
  onImageUpload,
  onDelete,
  canDelete = true,
  disabled = false,
}) => {
  // Local state for text fields - prevents focus loss on parent re-render
  const [localTitle, setLocalTitle] = useState(item.title);
  const [localSubtitle, setLocalSubtitle] = useState(item.subtitle);

  // Sync props to local state when external changes occur (e.g., reorder, undo)
  useEffect(() => {
    setLocalTitle(item.title);
  }, [item.title]);

  useEffect(() => {
    setLocalSubtitle(item.subtitle);
  }, [item.subtitle]);

  // Blur handlers for text fields
  const handleTitleBlur = useCallback(() => {
    if (localTitle !== item.title) {
      onChange('title', localTitle);
    }
  }, [localTitle, item.title, onChange]);

  const handleSubtitleBlur = useCallback(() => {
    if (localSubtitle !== item.subtitle) {
      onChange('subtitle', localSubtitle);
    }
  }, [localSubtitle, item.subtitle, onChange]);

  // Handle image change (URL or File)
  const handleImageChange = useCallback(
    async (value: string | File | null) => {
      if (value instanceof File) {
        if (onImageUpload) {
          try {
            const url = await onImageUpload(value);
            onChange('imageUrl', url);
          } catch (error) {
            console.error('Image upload failed:', error);
          }
        }
      } else {
        onChange('imageUrl', value || '');
      }
    },
    [onChange, onImageUpload]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: FIELD_GAP_SMALL,
        pt: 0.5,
        px: 1.5,
        pb: 1.5,
        bgcolor: 'background.paper',
        borderRadius: SPACING.sm,
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          borderColor: 'action.hover',
        },
      }}
      data-testid={`bullet-item-editor-${index}`}
    >
      {/* Header row: # + Delete */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Left side: # with fixed width, space for drag handle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ width: 24, flexShrink: 0 }}
          >
            #{index + 1}
          </Typography>
          {/* Spacer for drag handle (appears via parent SortableList.Item) */}
          <Box sx={{ width: 24, flexShrink: 0 }} />
        </Box>

        {/* Right side: delete button */}
        {onDelete && (
          <Tooltip title={canDelete ? 'Delete item' : 'Minimum items reached'}>
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={disabled || !canDelete}
                data-testid={`bullet-item-${index}-delete`}
                sx={{
                  opacity: 0.5,
                  '&:hover': {
                    opacity: 1,
                    color: 'error.main',
                  },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Image row - full width */}
      <Box data-testid={`bullet-item-${index}-image`}>
        <MediaUploadField
          value={item.imageUrl || null}
          onChange={handleImageChange}
          mediaType="image"
          disabled={disabled}
          size="small"
          showPreview
          placeholder="Image URL"
        />
      </Box>

      {/* Title field with paywall variable support */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Title
        </Typography>
        <PaywallVariableTextField
          value={localTitle}
          onChange={setLocalTitle}
          onBlur={handleTitleBlur}
          disabled={disabled}
          size="small"
          fullWidth
          placeholder="Feature title (type @ for variables)"
          data-testid={`bullet-item-${index}-title`}
        />
      </Box>

      {/* Subtitle field with paywall variable support */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Subtitle
        </Typography>
        <PaywallVariableTextField
          value={localSubtitle}
          onChange={setLocalSubtitle}
          onBlur={handleSubtitleBlur}
          disabled={disabled}
          size="small"
          fullWidth
          placeholder="Feature description (type @ for variables)"
          data-testid={`bullet-item-${index}-subtitle`}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
              color: 'text.secondary',
            },
          }}
        />
      </Box>
    </Box>
  );
};

// Wrap in React.memo to prevent re-renders when parent struct changes
export const BulletItemEditor = memo(BulletItemEditorComponent);

export default BulletItemEditor;
